/**
 * Périmètre d'application d'une grille tarifaire.
 *
 * Une grille ne vaut pas toujours pour tout : celle-ci ne couvre que l'ADR,
 * celle-là que le froid, une troisième que les flux retour. Ce périmètre se
 * décrivait par un « scope » — une chaîne libre (MESSAGERIE, BIG_BAGS…) qu'il
 * fallait faire coïncider avec une colonne des expéditions, sans que rien ne
 * garantisse la correspondance : une faute de frappe ne produisait aucune
 * erreur, seulement une grille qui cotait ce qu'elle n'aurait pas dû.
 *
 * Il se décrit désormais par les valeurs mêmes des expéditions : on coche ce à
 * quoi la grille s'applique, dans des listes issues des expéditions importées.
 * Aucun critère = aucune restriction — c'est le défaut, et de loin le cas le
 * plus courant.
 */

/** Champs d'expédition sur lesquels une grille peut se restreindre. */
export const GRID_APPLICABILITY_FIELDS = [
  'incoterm',
  'typeOfGoods',
  'typeOfFlow',
  'transportMode',
  'means',
  'dangerousGoods',
  'temperatureControlled',
] as const;

export type GridApplicabilityField = (typeof GRID_APPLICABILITY_FIELDS)[number];

/** Champs à deux valeurs : on coche Oui / Non, pas une liste ouverte. */
export const GRID_APPLICABILITY_BOOLEAN_FIELDS: readonly GridApplicabilityField[] = [
  'dangerousGoods',
  'temperatureControlled',
];

/**
 * Valeur cochable représentant « champ non renseigné sur l'expédition ».
 *
 * Une restriction exclut par défaut les lignes qui ne portent pas la valeur —
 * y compris celles qui n'en portent aucune. Beaucoup de fichiers laissent ces
 * colonnes vides : sans case à cocher dédiée, restreindre une grille rendrait
 * silencieusement non cotable tout un dataset. La case rend le choix explicite.
 */
export const GRID_APPLICABILITY_EMPTY = '__EMPTY__';

/** Le périmètre lui-même : par champ, les valeurs couvertes. */
export type GridApplicability = Partial<Record<GridApplicabilityField, string[]>>;

/** Valeurs d'une expédition confrontées au périmètre d'une grille. */
export interface GridApplicabilityInput {
  incoterm?: string | null;
  typeOfGoods?: string | null;
  typeOfFlow?: string | null;
  transportMode?: string | null;
  /** Type de moyen (`means` Connect4ward : SEMI_44, PORTEUR_19T…), par son code. */
  means?: string | null;
  dangerousGoods?: boolean | null;
  temperatureControlled?: boolean | null;
}

/**
 * Forme canonique d'une valeur. « Big bags », « big-bags » et « BIG_BAGS »
 * désignent la même marchandise et ne s'écrivent jamais pareil : les comparer
 * telles quelles ferait échouer le rattachement en silence. La valeur brute
 * reste stockée et affichée telle que l'utilisateur l'a cochée.
 */
export function normalizeApplicabilityValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  const key = String(value)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return key || null;
}

/** Vrai si le champ se coche Oui / Non plutôt que dans une liste de valeurs. */
export function isBooleanApplicabilityField(field: GridApplicabilityField): boolean {
  return GRID_APPLICABILITY_BOOLEAN_FIELDS.includes(field);
}

/**
 * Nettoie un périmètre reçu du client : champs inconnus écartés, valeurs
 * vidées de leurs doublons, critère sans valeur retiré (un critère coché sur
 * rien ne restreint rien — le garder afficherait une restriction fantôme).
 */
export function normalizeGridApplicability(raw: unknown): GridApplicability {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const source = raw as Record<string, unknown>;
  const clean: GridApplicability = {};
  for (const field of GRID_APPLICABILITY_FIELDS) {
    const values = source[field];
    if (!Array.isArray(values)) continue;
    const kept: string[] = [];
    const seen = new Set<string>();
    for (const value of values) {
      if (typeof value !== 'string') continue;
      const trimmed = value.trim();
      if (!trimmed) continue;
      const key = trimmed === GRID_APPLICABILITY_EMPTY
        ? GRID_APPLICABILITY_EMPTY
        : normalizeApplicabilityValue(trimmed);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      kept.push(trimmed);
    }
    if (kept.length > 0) clean[field] = kept;
  }
  return clean;
}

/** Vrai dès qu'un critère est posé — donc que la grille ne vaut plus pour tout. */
export function isGridRestricted(applicability: GridApplicability | null | undefined): boolean {
  if (!applicability) return false;
  return GRID_APPLICABILITY_FIELDS.some((f) => (applicability[f]?.length ?? 0) > 0);
}

/** Les critères posés, dans l'ordre stable de `GRID_APPLICABILITY_FIELDS`. */
export function gridApplicabilityEntries(
  applicability: GridApplicability | null | undefined,
): { field: GridApplicabilityField; values: string[] }[] {
  if (!applicability) return [];
  return GRID_APPLICABILITY_FIELDS
    .filter((field) => (applicability[field]?.length ?? 0) > 0)
    .map((field) => ({ field, values: applicability[field] as string[] }));
}

/** Valeur d'expédition portée par un champ du périmètre. */
function inputValue(
  input: GridApplicabilityInput,
  field: GridApplicabilityField,
): string | boolean | null | undefined {
  return input[field];
}

/**
 * La grille s'applique-t-elle à cette expédition ?
 *
 * Chaque critère posé doit être satisfait (ET entre critères), et l'est dès
 * qu'une des valeurs cochées correspond (OU à l'intérieur d'un critère). Une
 * expédition dont le champ est vide n'entre dans le périmètre que si « (vide) »
 * a été coché — sans quoi une grille « marchandises dangereuses » couvrirait
 * les lignes dont on ignore si elles le sont.
 */
export function gridApplicabilityMatches(
  applicability: GridApplicability | null | undefined,
  input: GridApplicabilityInput,
  /** Critères mis de côté : la grille ne les restreint plus pour cette cotation (« tous les valeurs »). */
  ignored?: ReadonlySet<GridApplicabilityField>,
): boolean {
  for (const { field, values } of gridApplicabilityEntries(applicability)) {
    if (ignored?.has(field)) continue;
    const raw = inputValue(input, field);
    const actual = normalizeApplicabilityValue(raw ?? null);
    const wanted = new Set(
      values.map((v) => (v === GRID_APPLICABILITY_EMPTY
        ? GRID_APPLICABILITY_EMPTY
        : normalizeApplicabilityValue(v))),
    );
    const ok = actual === null
      ? wanted.has(GRID_APPLICABILITY_EMPTY)
      : wanted.has(actual);
    if (!ok) return false;
  }
  return true;
}

/** Valeur d'un critère telle que la compare la grille : « vide » ne s'accepte que si cochée. */
function scopeAccepts(
  applicability: GridApplicability | null | undefined,
  field: GridApplicabilityField,
  raw: string | boolean | null | undefined,
): boolean {
  const values = applicability?.[field];
  if (!values || values.length === 0) return true;
  const actual = normalizeApplicabilityValue(raw ?? null);
  const wanted = new Set(
    values.map((v) => (v === GRID_APPLICABILITY_EMPTY ? GRID_APPLICABILITY_EMPTY : normalizeApplicabilityValue(v))),
  );
  return actual === null ? wanted.has(GRID_APPLICABILITY_EMPTY) : wanted.has(actual);
}

/** Ce qu'on peut choisir pour un critère, vu des grilles qui peuvent coter. */
export interface ApplicabilityChoices {
  /**
   * Valeurs à proposer, dans la graphie des grilles (jamais « (vide) »). Les
   * critères Oui / Non rendent `'true'` / `'false'`. Vide : aucune grille
   * compatible ne restreint ce critère, il n'y a donc rien à choisir.
   */
  values: string[];
  /**
   * « Indifférent » est un choix valable : une grille compatible ne restreint
   * pas ce critère, ou accepte les expéditions qui ne le portent pas. Faux : le
   * critère est obligatoire — sans valeur, aucune grille ne cote.
   */
  allowAny: boolean;
}

/**
 * Choix possibles pour `field`, compte tenu des grilles qui peuvent coter et de
 * ce qui est déjà décidé sur les autres critères.
 *
 * Une grille est compatible quand elle accepte ce qui est décidé. Dans
 * `selection`, un critère **absent** (`undefined`) n'est pas encore décidé et
 * n'écarte aucune grille — sans quoi deux critères exigés ensemble par une même
 * grille se bloqueraient l'un l'autre. `null` est une décision : l'expédition
 * ne porte pas ce critère, et seules les grilles sans restriction dessus (ou
 * qui acceptent « vide ») restent compatibles. La sélection de `field`
 * lui-même est ignorée : on veut ce qu'il peut devenir, pas ce qu'il vaut.
 */
export function applicabilityChoices(
  scopes: readonly (GridApplicability | null | undefined)[],
  selection: GridApplicabilityInput,
  field: GridApplicabilityField,
): ApplicabilityChoices {
  const boolean = isBooleanApplicabilityField(field);
  const byKey = new Map<string, string>();
  let allowAny = false;

  for (const scope of scopes) {
    const compatible = GRID_APPLICABILITY_FIELDS.every(
      (other) => other === field || selection[other] === undefined || scopeAccepts(scope, other, selection[other]),
    );
    if (!compatible) continue;

    const restricted = scope?.[field];
    if (!restricted || restricted.length === 0) {
      allowAny = true;
      continue;
    }
    for (const raw of restricted) {
      if (raw === GRID_APPLICABILITY_EMPTY) {
        allowAny = true;
        continue;
      }
      const key = normalizeApplicabilityValue(raw);
      if (!key || byKey.has(key)) continue;
      if (boolean) {
        if (key === 'TRUE') byKey.set(key, 'true');
        else if (key === 'FALSE') byKey.set(key, 'false');
      } else {
        byKey.set(key, raw.trim());
      }
    }
  }

  const values = Array.from(byKey.values()).sort((a, b) =>
    a.localeCompare(b, 'fr', { sensitivity: 'base' }),
  );
  return { values, allowAny };
}

/**
 * Empreinte d'un périmètre, pour dire que deux grilles couvrent exactement la
 * même chose : c'est elle qui décide qu'activer une grille en archive une
 * autre. Indépendante de l'ordre de saisie et de la casse.
 */
export function gridApplicabilitySignature(
  applicability: GridApplicability | null | undefined,
): string {
  const parts = gridApplicabilityEntries(applicability).map(({ field, values }) => {
    const keys = values
      .map((v) => (v === GRID_APPLICABILITY_EMPTY ? GRID_APPLICABILITY_EMPTY : normalizeApplicabilityValue(v)))
      .filter((v): v is string => !!v)
      .sort();
    return `${field}=${Array.from(new Set(keys)).join('|')}`;
  });
  return parts.join(';');
}

/**
 * Libellés français des critères. Le back s'en sert pour les messages qu'il
 * compose lui-même (alertes d'opérations de masse, journaux) ; l'interface,
 * elle, passe par ses propres traductions.
 */
export const GRID_APPLICABILITY_FIELD_LABELS_FR: Record<GridApplicabilityField, string> = {
  incoterm: 'Incoterm',
  typeOfGoods: 'Type de marchandises',
  typeOfFlow: 'Type de flux',
  transportMode: 'Mode de transport',
  means: 'Type de moyen',
  dangerousGoods: 'Marchandises dangereuses',
  temperatureControlled: 'Température dirigée',
};

/**
 * Libellé d'affichage d'une valeur de critère, quand il diffère de la valeur
 * stockée : c'est le cas du moyen, stocké par son code (SEMI_44) mais lu par son
 * nom. Rend `undefined` pour laisser la valeur telle quelle.
 */
export type ApplicabilityValueLabeler = (
  field: GridApplicabilityField,
  value: string,
) => string | null | undefined;

/** Valeur telle qu'on la lit dans une phrase. */
function readableValue(
  field: GridApplicabilityField,
  value: string,
  labeler?: ApplicabilityValueLabeler,
): string {
  if (value === GRID_APPLICABILITY_EMPTY) return '(vide)';
  if (isBooleanApplicabilityField(field)) {
    const key = normalizeApplicabilityValue(value);
    if (key === 'TRUE') return 'Oui';
    if (key === 'FALSE') return 'Non';
  }
  return labeler?.(field, value) || value;
}

/**
 * Périmètre en une ligne : « Type de marchandises : PALETTE, SACS ·
 * Marchandises dangereuses : Non ». Sans critère, dit explicitement que la
 * grille vaut pour tout — une case vide laisserait penser à un oubli.
 */
export function describeGridApplicability(
  applicability: GridApplicability | null | undefined,
  allLabel = 'Toutes les expéditions',
  labeler?: ApplicabilityValueLabeler,
): string {
  const entries = gridApplicabilityEntries(applicability);
  if (entries.length === 0) return allLabel;
  return entries
    .map(({ field, values }) =>
      `${GRID_APPLICABILITY_FIELD_LABELS_FR[field]} : ${values.map((v) => readableValue(field, v, labeler)).join(', ')}`)
    .join(' · ');
}
